import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    ImageResourceItem,
    Inject, ModelEvents,
} from '@denishussar/html5-game-engine';
import { Point, Rectangle } from 'pixi.js';
import { AnimationContents } from '../../../AnimationContents';
import { ButtonController, IButtonControllerOptions } from '../../Common/ButtonController';
import { Easing } from '../../Common/Easing';
import { ModalsNames } from '../../Common/MadalsManager/ModalsList';
import { LocalizationContents } from '../../LocalizationContents';
import { GameLogic } from '../../Logic/GameLogic';
import { GameModel, GameStateEnum, InstrumentTypeEnum } from '../../Models/GameModel';
import { ITopBarMenuControllerOptions, TopBarMenuController } from '../Screens/TopBarMenuController';
import { BoosterListController } from '../UI/BoosterListController';
import { PaletteController } from '../UI/PaletteController';
import { GameViewportController } from './GameViewportController';
import { SavesManager } from '../../Managers/SavesManager';
import {
    GameEvent,
    GameEventPopupButtonName,
    GameEventPopupName,
    GameEventSceneButtonClick,
} from '../../Constants/GameEvent';
import isMobile from 'is-mobile';
import { PaletteControllerMobile } from '../UI/PaletteControllerMobile';

export interface IGameScreenControllerOptions {
    opacity: boolean;
}

export class GameScreenController extends AnimationBaseController {
    $model: GameModel;

    protected paletteController: PaletteController;
    protected topBarMenu: TopBarMenuController;
    protected minusButton: ButtonController;
    protected plusButton: ButtonController;
    protected btns_booster: BoosterListController;
    protected viewportController: GameViewportController;
    private fpsMeter = document.createElement("label");
    protected easings: Array<Easing> = [];

    protected interactiveGlobalRectangle = new Rectangle();

    public constructor(
        @Inject protected options: IGameScreenControllerOptions,
        @Inject protected gameLogic: GameLogic,
        @Inject protected saveManager: SavesManager,
        @Inject protected modelEvents: ModelEvents,
        @Inject protected model: GameModel,
    ) {
        super('GameScreenController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        const resources = {
            'frame': new ImageResourceItem('frame', 'picture_frame.png'),
            'spinner': new ImageResourceItem('spinner', 'spinner.png'),
        };

        return <IAnimationControllerConfiguration>{
            animationContent: isMobile() ? AnimationContents.GAME_SCREEN_MOBILE_JSON : AnimationContents.GAME_SCREEN_JSON,
            resources,
            libraryName: isMobile() ? 'game_screen_mobile' : 'game_screen',
            childControllers: {
                viewport: {
                    controller: GameViewportController,
                    model: this.$model,
                    options: {
                        interactiveGlobalRectangle: this.interactiveGlobalRectangle,
                    },
                },
                topBarMenu: {
                    controller: TopBarMenuController,
                    model: this.$model,
                    options: <ITopBarMenuControllerOptions>{
                        showLives: false,// true,
                        showCoins: true,
                        showSettings: true,
                        isGameScreenParent: true,
                        onClickExit: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.popupOpen, { popupName: 'Exit', status });

                            if (!status) {
                                return;
                            }

                            this.gameLogic.modalsManager.show(ModalsNames.confirm, {
                                withoutEvent: true,
                                text: LocalizationContents.getText('ExitGameTitle'),
                                yesText: LocalizationContents.getText('ExitGameNoButton'),
                                noText: LocalizationContents.getText('ExitGameYesButton'),
                                yesClick: () => {
                                    this.gameLogic.modalsManager.hide()
                                    this.modelEvents.dispatch(this.$model, GameEvent.popupButtonClick, { popupName: GameEventPopupName.exit, buttonName: GameEventPopupButtonName.cancel });
                                },
                                noClick: async () => {
                                    this.model.timeSpent = this.model.timeSpent + Math.floor((Date.now() - this.model.timeStart) / 1000);
                                    this.model.timeStart = Date.now();

                                    this.modelEvents.dispatch(this.$model, GameEvent.popupButtonClick, { popupName: GameEventPopupName.exit, buttonName: GameEventPopupButtonName.exit });

                                    this.modelEvents.dispatch(this.$model, GameEvent.levelAbort, {
                                        levelName: this.model.picture.name,
                                        levelSize: this.model.currentPictureWidth,
                                        boosters: {
                                            needle: this.model.userBalanceBoosterLine,
                                            yarn: this.model.userBalanceBoosterBrush,
                                            bucket: this.model.userBalanceBoosterBucket
                                        },
                                        stitchCount: this.model.filledPixelsCount,
                                        dragCount: this.model.dragCount,
                                        needleCount: this.model.needleCount,
                                        yarnCount: this.model.yarnCount,
                                        zoomInCount: this.model.zoomInCount,
                                        zoomOutCount: this.model.zoomOutCount,
                                        storeCount: this.model.storeCount,
                                        percentage: this.gameLogic.getPercentageProgress(this.saveManager.getSavePicture(this.model.picture.id)),
                                        timeSpent: this.model.timeSpent,
                                        rewardsCount: this.model.rewardsCount,
                                    });

                                    await this.gameLogic.modalsManager.hide();
                                    this.gameLogic.setState(GameStateEnum.main);
                                },
                            });
                        },
                    },
                },
                palette_container: {
                    instanceName: 'palette_container',
                    controller: isMobile() ? PaletteControllerMobile : PaletteController,
                    model: this.$model,
                },
                btns_booster: {
                    instanceName: 'btns_booster',
                    controller: BoosterListController,
                    model: this.$model,
                },
                btn_plus: {
                    instanceName: 'btn_plus',
                    controller: ButtonController,
                    model: this.$model,
                    options: <IButtonControllerOptions>{
                        onClick: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.sceneButtonClick, <GameEventSceneButtonClick>{ buttonName: 'Zoom In', status });

                            if (!status) {
                                return;
                            }

                            this.viewportController.zoom(false);
                        },
                    },
                },
                btn_minus: {
                    instanceName: 'btn_minus',
                    controller: ButtonController,
                    model: this.$model,
                    options: <IButtonControllerOptions>{
                        onClick: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.sceneButtonClick, <GameEventSceneButtonClick>{ buttonName: 'Zoom Out', status });

                            if (!status) {
                                return;
                            }

                            this.viewportController.zoom(true);
                        },
                    },
                },
            },
        };
    }

    protected onInitialize(): void {
        if (!PIXI.ticker.shared.started) {
            PIXI.ticker.shared.start();
        }

        this.gameLogic.setInstrumentType(InstrumentTypeEnum.preview);

        this.paletteController = this.$childControllers.get<PaletteController>('palette_container');

        this.topBarMenu = this.$childControllers.get('topBarMenu');
        this.minusButton = this.$childControllers.get('btn_minus');
        this.plusButton = this.$childControllers.get('btn_plus');
        this.btns_booster = this.$childControllers.get('btns_booster');

        const viewportController = this.$childControllers.get<GameViewportController>('viewport');

        if (isMobile()) {
            viewportController.display.position.set( 100, 0);
        } else {
            viewportController.display.position.set(0, -100);
        }

        setTimeout(() => {
            viewportController.display.parent.setChildIndex(viewportController.display, 0);
        }, 100);
        this.viewportController = viewportController;

        this.getDisplayObjectByQuery('booster_help')[0].visible = false;
        this.renderer.stage.addChildAt(this.getDisplayObjectByQuery('background')[0], 0);

        if (this.options.opacity) {
            this.display.alpha = 0;
        }

        Object.assign(this.fpsMeter.style, {
            position: "fixed",
            left: "1em",
            top: "1em",
            color: "#ff00ff",
            userSelect: "none",
            pointerEvents: "none",
        });
        // document.body.append(this.fpsMeter);
    }

    protected onDestroy() {
        const bg = this.getDisplayObjectByQuery('background')[0];

        if (bg) {
            bg.parent.removeChild(bg);
        }

        super.onDestroy();
    }

    protected onResize(width: number, height: number, scale: number) {
        this.scaleX = this.scaleY = Math.min(
            width / scale / this.$model.gameWidth,
            height / scale / this.$model.gameHeight,
        );

        const margin = 20;

        const topLeftPoint = this.display.toLocal(new Point(0, 0), this.renderer.stage);
        const topRightPoint = this.display.toLocal(new Point(width, 0), this.renderer.stage);
        const bottomLeftPoint = this.display.toLocal(new Point(0, height), this.renderer.stage);

        const globalTR = this.display.toGlobal(topRightPoint);
        const globalBL = this.display.toGlobal(new Point(0, bottomLeftPoint.y - 300 + 70));

        this.interactiveGlobalRectangle.x = 0;
        this.interactiveGlobalRectangle.y = 0;
        this.interactiveGlobalRectangle.width = globalTR.x;
        this.interactiveGlobalRectangle.height = globalBL.y;

        if (isMobile()) {
            this.setPosByQuery('palette_container', topLeftPoint.x, topLeftPoint.y);
        } else {
            this.setPosByQuery('palette_container', topLeftPoint.x, bottomLeftPoint.y + 70);
        }

        this.setPosByQuery('btns_booster', topRightPoint.x, 0);

        if (isMobile()) {
            this.setPosByQuery('btn_minus',
                bottomLeftPoint.x + this.minusButton.display.width / 2 + 320,
                bottomLeftPoint.y -
                this.minusButton.display.height / 2 -
                margin);

            this.setPosByQuery('btn_plus',
                this.minusButton.display.x,
                this.minusButton.display.y -
                this.minusButton.display.height / 2 -
                this.plusButton.display.height / 2 -
                margin);
        } else {
            this.setPosByQuery('btn_minus',
                bottomLeftPoint.x + this.minusButton.display.width / 2 + margin,
                bottomLeftPoint.y -
                this.paletteController.display.height -
                this.minusButton.display.height / 2 -
                margin);

            this.setPosByQuery('btn_plus',
                this.minusButton.display.x,
                this.minusButton.display.y -
                this.minusButton.display.height / 2 -
                this.plusButton.display.height / 2 -
                margin);
        }

        const backgroundScale = Math.max(
            width / this.$model.gameWidth,
            height / this.$model.gameHeight,
        );

        this.setPosByQuery('background', width / 2, height / 2);
        this.setScaleByQuery('background', backgroundScale, backgroundScale);

        const topBar = this.$childControllers.get('topBarMenu') as TopBarMenuController;
        topBar.display.scale.set(1 / this.scaleX);

        if (isMobile()) {
            this.topBarMenu.setPaletteWidth(this.paletteController.display.getBounds().width * 0.75);
            this.paletteController.setBackgroundSize(bottomLeftPoint.y * 2);

        } else {
            this.topBarMenu.setPaletteWidth(0);
            this.paletteController.setBackgroundSize(topRightPoint.x * 2);
        }
    }

    protected onUpdate(frameRate: number) {
        this.easings.forEach((easing) => {
            easing.update(frameRate);
        });
        this.fpsMeter.innerHTML = frameRate.toFixed(3);
    }
}
